/**
 * XeroMappingPage — Sprint 24 F007
 *
 * Route: /financial-periods/:periodId/xero-mapping?entityRecordId=<uuid>&connectionId=<uuid>
 *
 * Hosts the XeroMappingEditor component for reviewing and saving Xero
 * account category mappings before generating standardised financials.
 */

import { useRoute, useSearch } from "wouter";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  XeroMappingEditor,
  type MappingSuggestion,
} from "@/components/financial/import/xero-mapping-editor";

function goBack(): void {
  window.history.back();
}

export default function XeroMappingPage() {
  const [, params] = useRoute("/financial-periods/:periodId/xero-mapping");
  const search = useSearch();

  const periodId = params?.periodId ?? "";
  const searchParams = new URLSearchParams(search ?? "");
  const entityRecordId = searchParams.get("entityRecordId") ?? "";
  const connectionId = searchParams.get("connectionId") ?? "";

  // The component loads existing saved mappings from GET /api/xero/mappings.
  // Suggested mappings are passed in when navigating from the sync step.
  const suggestedMappings: MappingSuggestion[] = [];

  if (!periodId || !entityRecordId) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm text-destructive">
            Missing required parameters: financialPeriodId and entityRecordId.
          </p>
          <Button variant="outline" size="sm" onClick={goBack}>
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl mx-auto py-6 px-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Xero Account Mapping</CardTitle>
          <p className="text-sm text-muted-foreground">
            Review and adjust the category mappings for your Xero accounts
            before generating standardised financials.
          </p>
        </CardHeader>
        <CardContent>
          <XeroMappingEditor
            financialPeriodId={periodId}
            entityRecordId={entityRecordId}
            connectionId={connectionId}
            suggestedMappings={suggestedMappings}
            onComplete={goBack}
            onBack={goBack}
            onCancel={goBack}
          />
        </CardContent>
      </Card>
    </div>
  );
}
